import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { useIsMobile } from 'pricing-pages-lib/js/context/IsMobileContext.tsx';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { PPUIGrid } from 'pricing-pages-ui-components/grid/PPUIGrid';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { PriceDisplayStyled } from 'pricing-pages-ui-components/price/PriceDisplayStyled';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { PRODUCT_CARD } from '../../constants/tracking/TrackingSources';
import SingleProductCTA from '../../features/purchaseMotions/components/SingleProductCTA';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import { useBundledProductPurchaseMotions } from '../../features/purchaseMotions/hooks/useBundledProductPurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../hooks/useBulkPurchaseOptions';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppSelector } from '../../reducers';
import { showIncludedSeatsDisplaySelector } from '../../selectors/MerchCardSelectors';
import { getIsLatestVersionOfProductInProductInventorySelector, hasHigherProductLevelInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { getIsTBSkuSelector, productSelector } from '../../selectors/ProductsSelectors';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import DisabledDowngradeButton from '../buttons/DisabledDowngradeButton';
import ProductIcon from '../common/ProductIcon';
import SignupButton from '../common/SignupButton';
import { default as DetailedTermSelector } from '../common/TermSelectors/DetailedTermSelector';
import CurrentPlanTag from '../common/badges/CurrentPlanTag';
import { default as SkeletonLoadWrapper } from '../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import MobileSuiteBusinessObjectives from '../mobile/MobileSuiteBusinessObjectives';
import RequiredOnboardingSubtextContainer from '../plan-builder/RequiredOnboardingSubtextContainer';
import IncludedSeatsDisplay from './IncludedSeatsDisplay';
import { MerchCardWrapper } from './MerchCardSharedComponents';
import ProductBusinessObjectives from './business-objectives/ProductBusinessObjectives';
import SuiteBusinessObjectives from './business-objectives/SuiteBusinessObjectives';
import HighlightedFreePriceDisplay from './price-display/HighlightedFreePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const ProductTile = ({
  apiName,
  screen,
  trackingSource = PRODUCT_CARD,
  renderPrimaryBadge,
  renderSecondaryBadge,
  i18nKeyOverride,
  productNameOverride,
  gridItemProps,
  skipInterstitialPage = false
}) => {
  const isAppReady = useGlobalLoading();
  const isMobile = useIsMobile();
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const product = useAppSelector(state => {
    if (!isAppReady) return;
    return productSelector(state, {
      apiName
    });
  });
  const {
    currentSkuId: skuId,
    productLevel,
    productLine
  } = product || {};
  const isHubspotFree = apiName === HUBSPOT_FREE;
  const hasHigherTieredProduct = useAppSelector(state => {
    if (!productLevel) {
      return false;
    }
    return hasHigherProductLevelInProductInventorySelector(state, {
      productLevel,
      productLine
    });
  });
  const isCurrentPlanLevel = useAppSelector(state => isAppReady && apiName ? getIsLatestVersionOfProductInProductInventorySelector(state, {
    productIdentifier: {
      apiName
    }
  }) : false);
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    purchaseOptionsForTerm
  } = usePurchaseableProductsForTerm({
    queryParamTerm,
    apiName: product === null || product === void 0 ? void 0 : product.apiName
  });
  const shouldShowTermSelector = useAppSelector(state => isAppReady && skuId ? shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }) : false);
  const showIncludedSeatsDisplay = useAppSelector(state => isAppReady && skuId ? showIncludedSeatsDisplaySelector(state, {
    skuId
  }) : false);
  const purchaseMotions = useBundledProductPurchaseMotions({
    skuId
  });
  const isSuiteProduct = useAppSelector(state => isAppReady && skuId ? getIsTBSkuSelector(state, {
    skuId
  }) : false);
  if (!isAppReady || !product || !apiName || !skuId || !purchaseOptionsForTerm) {
    return null;
  }
  const i18nKey = i18nKeyOverride || `productTile.${apiName}`;
  const productName = productNameOverride || productLine;
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  const renderBusinessObjectives = () => {
    const shouldShowSuiteObjectives = (isHubspotFree || isSuiteProduct) && productLevel && productLevel !== 'BASIC';
    if (shouldShowSuiteObjectives) {
      return isMobile ? /*#__PURE__*/_jsx(MobileSuiteBusinessObjectives, {
        productLevel: productLevel
      }) : /*#__PURE__*/_jsx(SuiteBusinessObjectives, {
        productLevel: productLevel
      });
    }
    return /*#__PURE__*/_jsx(ProductBusinessObjectives, {
      apiName: apiName,
      collapsibleBusinessObjectives: isMobile
    });
  };
  const renderPriceComponent = () => {
    if (isHubspotFree) {
      return /*#__PURE__*/_jsx(HighlightedFreePriceDisplay, {});
    }
    return /*#__PURE__*/_jsx(PriceDisplayStyled, {
      pricingInfo: purchaseOptionsForTerm,
      seleniumId: apiName || 'loading',
      currencyCode: currencyCode,
      currencyCodeUnit: currencyCodeUnit
    });
  };
  const renderCTAs = () => {
    if (isHubspotFree) {
      return /*#__PURE__*/_jsx(SignupButton, {
        trackingSource: trackingSource,
        sourceId: trackingSource,
        nonMerchandiseIdProduct: apiName
      });
    }
    if (hasHigherTieredProduct) {
      return /*#__PURE__*/_jsx(DisabledDowngradeButton, {});
    }
    return purchaseMotions.map((purchaseMotion, index) => /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(SingleProductCTA, Object.assign({
        use: index === 0 ? 'primary' : 'secondary',
        purchaseMotion: purchaseMotion,
        apiName: apiName,
        screen: screen,
        trackingSource: trackingSource,
        purchaseMotionLocation: 'MERCH_CARD',
        skipInterstitialPage: skipInterstitialPage
      }, ctaVariantProps))
    }, purchaseMotion));
  };
  const renderTileContent = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        "data-selenium-id": `${apiName}-merch-card`,
        paddingBlockStart: "800",
        position: "relative",
        showBorderTop: isMobile,
        gridRowStart: 1,
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [isCurrentPlanLevel ? /*#__PURE__*/_jsx(CurrentPlanTag, {}) : null, renderPrimaryBadge ? renderPrimaryBadge() : null, renderSecondaryBadge ? renderSecondaryBadge() : null, /*#__PURE__*/_jsxs(Flex, {
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: "start",
            gap: "200",
            children: [productName && /*#__PURE__*/_jsx(View, {
              marginBlockStart: isMobile ? '0' : '100',
              children: /*#__PURE__*/_jsx(ProductIcon, {
                iconStyle: {
                  responsive: false,
                  blockSize: '24px'
                },
                name: productName,
                tier: productLevel
              })
            }), /*#__PURE__*/_jsx(SectionTitle, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `${i18nKey}.title`
              })
            })]
          })]
        })
      })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        paddingBlockStart: "600",
        gridRowStart: 2,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${i18nKey}.description`
        })
      })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        paddingBlockStart: "400",
        gridRowStart: 3,
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [20, 33, 20],
          widths: [60, 80, 140],
          children: renderPriceComponent()
        })
      })), showIncludedSeatsDisplay ? /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        paddingBlockStart: "400",
        gridRowStart: 4,
        children: /*#__PURE__*/_jsx(IncludedSeatsDisplay, {
          skuId: skuId,
          queryParamTerm: queryParamTerm
        })
      })) : null, /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        paddingBlockStart: "400",
        gridRowStart: 5,
        children: shouldShowTermSelector ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [41],
          widths: ['100%'],
          children: /*#__PURE__*/_jsx(DetailedTermSelector, {
            apiName: apiName,
            onChange: setQueryParamTerm,
            activeTerm: queryParamTerm,
            trackingSource: trackingSource
          })
        }) : null
      })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        paddingBlockStart: "400",
        paddingBlockEnd: "600",
        gridRowStart: 6,
        marginBlockEnd: isMobile ? '0' : '700',
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [47],
          widths: ['100%'],
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
              apiName: apiName,
              screen: screen,
              trackingSource: trackingSource
            }),
            children: /*#__PURE__*/_jsx(Flex, {
              flexDirection: "column",
              justifyContent: "center",
              gap: "200",
              inlineSize: "100%",
              children: renderCTAs()
            })
          })
        })
      })), /*#__PURE__*/_jsx(PPUIGrid, Object.assign({}, gridItemProps, {
        gridTemplateRows: "subgrid",
        gridRowStart: 7,
        gridRowEnd: 9,
        marginBlockEnd: isMobile ? '0' : '700',
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [47, 47],
          widths: ['100%', '100%'],
          children: renderBusinessObjectives()
        })
      })), skuId ? /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
        paddingBlockStart: "400",
        paddingBlockEnd: "600",
        gridRowStart: 10,
        showBorderBottom: isMobile,
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          alignItems: "center",
          children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
            queryParamTerm: queryParamTerm,
            skuIds: [skuId],
            trackingSource: trackingSource,
            includesOnboardingCost: false
          })
        })
      })) : null]
    });
  };
  if (isMobile) {
    return renderTileContent();
  }
  return /*#__PURE__*/_jsx(MerchCardWrapper, {
    children: renderTileContent()
  });
};
export default ProductTile;